import { useEffect, useRef, useState } from 'react';
import { AqlVisionMark } from './aql-vision-brand';

const INTRO_DURATION_MS = 2_450;
const CURTAIN_DURATION_MS = 900;

export function AqlVisionIntro() {
  const [visible, setVisible] = useState(true);
  const [opening, setOpening] = useState(false);
  const [started, setStarted] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!visible) return;

    const audio = new Audio('/assets/aql-vision-welcome.wav');
    let disposed = false;
    audio.volume = 0.72;
    audio.preload = 'auto';
    audioRef.current = audio;

    void audio.play()
      .then(() => { if (!disposed) setStarted(true); })
      .catch(() => { if (!disposed) setAutoplayBlocked(true); });

    return () => {
      disposed = true;
      // Let a sound unlocked by the user's gesture finish after the curtain opens.
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!visible || !started) return;
    const openingTimer = window.setTimeout(() => setOpening(true), INTRO_DURATION_MS);
    const hideTimer = window.setTimeout(
      () => setVisible(false),
      INTRO_DURATION_MS + CURTAIN_DURATION_MS,
    );
    return () => {
      window.clearTimeout(openingTimer);
      window.clearTimeout(hideTimer);
    };
  }, [started, visible]);

  const startWithSound = () => {
    const audio = audioRef.current;
    if (!audio) return;
    void audio.play().then(() => {
      setAutoplayBlocked(false);
      setStarted(true);
      setOpening(true);
      window.setTimeout(() => setVisible(false), CURTAIN_DURATION_MS);
    }).catch(() => {});
  };

  const dismiss = () => {
    if (!started) {
      startWithSound();
      return;
    }
    const audio = audioRef.current;
    if (audio?.paused) void audio.play().catch(() => {});
    if (opening) return;
    setOpening(true);
    window.setTimeout(() => setVisible(false), CURTAIN_DURATION_MS);
  };

  if (!visible) return null;

  return (
    <button
      type="button"
      className={`aql-vision-intro${opening ? ' is-opening' : ''}`}
      onClick={dismiss}
      aria-label="Avançar para a aplicação"
    >
      <span className="aql-vision-intro__curtain aql-vision-intro__curtain--left" aria-hidden="true">
        <video className="aql-vision-intro__scene" autoPlay muted loop playsInline>
          <source src="/aql-entry-serenity.mp4" type="video/mp4" />
        </video>
      </span>
      <span className="aql-vision-intro__curtain aql-vision-intro__curtain--right" aria-hidden="true">
        <video className="aql-vision-intro__scene" autoPlay muted loop playsInline>
          <source src="/aql-entry-serenity.mp4" type="video/mp4" />
        </video>
      </span>
      <span className="aql-vision-intro__grid" aria-hidden="true" />
      <span className="aql-vision-intro__pulse aql-vision-intro__pulse--one" aria-hidden="true" />
      <span className="aql-vision-intro__pulse aql-vision-intro__pulse--two" aria-hidden="true" />
      <span className="aql-vision-intro__scan" aria-hidden="true">
        <AqlVisionMark className="aql-vision-intro__mark" alt="" />
      </span>
      <span className="aql-vision-intro__copy">
        <strong>AQL Vision</strong>
      </span>
      {autoplayBlocked && !started && (
        <span className="aql-vision-intro__start">Clique para iniciar</span>
      )}
    </button>
  );
}
